/** Most pages one signed form may be split into (mirrors the backend limit). */
export const MAX_CONSENT_PAGES = 10;

const MAX_EDGE_PX = 2200;
const JPEG_QUALITY = 0.86;

export function isPdfFile(file) {
  const type = String(file?.type || "").toLowerCase();
  const name = String(file?.name || "").toLowerCase();
  return type === "application/pdf" || name.endsWith(".pdf");
}

export function isImageFile(file) {
  const type = String(file?.type || "").toLowerCase();
  if (type.startsWith("image/")) return true;
  return /\.(jpe?g|png|webp|heic|heif)$/i.test(String(file?.name || ""));
}

export function isAcceptedConsentFile(file) {
  return isPdfFile(file) || isImageFile(file);
}

/** Target size that keeps the long edge readable but bounded. */
export function scaledSize(width, height, maxEdge = MAX_EDGE_PX) {
  const w = Number(width) || 0;
  const h = Number(height) || 0;
  if (!w || !h) return { width: w, height: h };
  const longEdge = Math.max(w, h);
  if (longEdge <= maxEdge) return { width: w, height: h };
  const ratio = maxEdge / longEdge;
  return { width: Math.round(w * ratio), height: Math.round(h * ratio) };
}

export function pageFileName(index, ext = "jpg") {
  const stamp = new Date().toISOString().slice(0, 10);
  return `consent-${stamp}-p${index + 1}.${ext}`;
}

function canvasToBlob(canvas, type, quality) {
  return new Promise((resolve) => {
    if (typeof canvas.toBlob !== "function") {
      resolve(null);
      return;
    }
    canvas.toBlob((blob) => resolve(blob), type, quality);
  });
}

function loadImage(file) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve(img);
    };
    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error("decode_failed"));
    };
    img.src = url;
  });
}

/** Draw a source (image or video frame) onto a canvas, downscaled to a JPEG File. */
export async function drawToJpegFile(source, srcWidth, srcHeight, name) {
  const { width, height } = scaledSize(srcWidth, srcHeight);
  if (!width || !height) return null;
  const canvas = document.createElement("canvas");
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext("2d");
  if (!ctx) return null;
  ctx.fillStyle = "#fff";
  ctx.fillRect(0, 0, width, height);
  ctx.drawImage(source, 0, 0, width, height);
  const blob = await canvasToBlob(canvas, "image/jpeg", JPEG_QUALITY);
  if (!blob) return null;
  return new File([blob], name, { type: "image/jpeg", lastModified: Date.now() });
}

/**
 * Normalise a picked or captured file into an upload-ready page.
 * PDFs and images the browser cannot decode (e.g. HEIC outside Safari) are
 * kept as-is so the original evidence is never lost.
 */
export async function prepareConsentPage(file, index = 0) {
  if (!file || isPdfFile(file) || !isImageFile(file)) return file;
  try {
    const img = await loadImage(file);
    const out = await drawToJpegFile(
      img,
      img.naturalWidth,
      img.naturalHeight,
      pageFileName(index)
    );
    if (!out) return file;
    return out.size < file.size ? out : file;
  } catch {
    return file;
  }
}
